import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react'
import type { Guide } from '@/lib/guides/types/guide'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { type GridSnapLiveRect, snapShapeCreationPosition } from '@/lib/guides/grid-snap'
import {
  type SmartGuideLine,
  computeSmartGuides as computeSmartGuidesForCreation,
  extractLayoutSnapLines,
} from '@/lib/guides/smart-guides'
import {
  buildMirroredShapePathCommands,
  computeShapeCreationGeometry,
} from '../helpers/shape-creation-geometry'
import type { DragCreateState } from '../types/drag-create-state'

/**
 * Shape-tool pasteboard gestures: drag (or click) to create a shape of the picked type, with
 * Shift/Alt modifiers, snap-to-grid and smart-guide snapping on release.
 * `layoutSnapLinesForDragCreateRef` is created here so the release handler can read it, and kept
 * current by `use-snap-guides`.
 */
export function useShapeDragCreate({
  internalShapeManagerRef,
  isInShapeCreationMode,
  selectedShape,
  scaleRef,
  getCanvasPointFromPointerEvent,
  showGuides,
  snapToGuides,
  snapGuideDistance,
  snapToGrid,
  documentGridSnapStepPt,
  gridSnapLiveRects,
  setActiveSmartGuides,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  isInShapeCreationMode: boolean
  selectedShape: ShapeType | null
  scaleRef: MutableRefObject<number>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  showGuides: boolean
  snapToGuides: boolean
  snapGuideDistance: number
  snapToGrid: boolean
  documentGridSnapStepPt: number
  gridSnapLiveRects: GridSnapLiveRect[]
  setActiveSmartGuides: Dispatch<SetStateAction<SmartGuideLine[]>>
}) {
  const [dragCreate, setDragCreate] = useState<DragCreateState | null>(null)
  const dragCreateRef = useRef<DragCreateState | null>(null)
  const dragRafRef = useRef<number | null>(null)
  /**
   * Latest layout lines for smart guides; assigned after `snapGuides` is computed (see
   * `use-snap-guides`).
   */
  const layoutSnapLinesForDragCreateRef = useRef(extractLayoutSnapLines([] as Guide[]))

  const computeShapeCreationParams = useCallback(
    (drag: DragCreateState, shapeType: ShapeType, mode: 'create' | 'preview') =>
      computeShapeCreationGeometry(drag, shapeType, mode, scaleRef.current),
    [scaleRef]
  )

  const buildShapePathCommands = useCallback(
    (shapeType: ShapeType, width: number, height: number, mirror?: { x: boolean; y: boolean }) =>
      buildMirroredShapePathCommands(shapeType, width, height, mirror),
    []
  )

  const handleShapeDragCreatePointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // If not in shape creation and not in select tool, ignore pasteboard pointer interaction.
      if (!isInShapeCreationMode || !selectedShape) return

      const mgr = internalShapeManagerRef.current

      if (!mgr) return

      if (e.button !== 0) return

      if (mgr.isInteracting()) return

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return

      e.preventDefault()
      e.currentTarget.setPointerCapture(e.pointerId)

      const newDrag: DragCreateState = {
        pointerId: e.pointerId,
        startCanvasX: pt.canvasX,
        startCanvasY: pt.canvasY,
        currentCanvasX: pt.canvasX,
        currentCanvasY: pt.canvasY,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
      }

      dragCreateRef.current = newDrag
      setDragCreate(newDrag)
    },
    [getCanvasPointFromPointerEvent, isInShapeCreationMode, selectedShape, internalShapeManagerRef]
  )

  const handleShapeDragCreatePointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Shape creation drag — use ref for guard checks (avoids stale closure)
      // and RAF-throttle state updates to prevent excessive re-renders.
      const drag = dragCreateRef.current

      if (!drag) return
      if (e.pointerId !== drag.pointerId) return

      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) return

      e.preventDefault()

      dragCreateRef.current = {
        ...drag,
        currentCanvasX: pt.canvasX,
        currentCanvasY: pt.canvasY,
        shiftKey: e.shiftKey,
        altKey: e.altKey,
      }

      if (dragRafRef.current == null) {
        dragRafRef.current = requestAnimationFrame(() => {
          dragRafRef.current = null
          setDragCreate(dragCreateRef.current)
        })
      }
    },
    [getCanvasPointFromPointerEvent]
  )

  const handleShapeDragCreatePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Cancel any pending RAF before reading final state
      if (dragRafRef.current != null) {
        cancelAnimationFrame(dragRafRef.current)
        dragRafRef.current = null
      }

      // Use the ref for the latest drag position (may not have been flushed to state yet)
      const finalDrag = dragCreateRef.current

      if (!finalDrag) return
      if (e.pointerId !== finalDrag.pointerId) return

      const mgr = internalShapeManagerRef.current

      if (!mgr) {
        dragCreateRef.current = null
        setDragCreate(null)
        return
      }

      if (selectedShape) {
        const params = computeShapeCreationParams(finalDrag, selectedShape, 'create')
        const styleOverrides = mgr.getLastUsedStyles()

        let createX = params.x
        let createY = params.y

        if (snapToGrid && documentGridSnapStepPt > 0) {
          const snapped = snapShapeCreationPosition(
            createX,
            createY,
            params,
            documentGridSnapStepPt,
            gridSnapLiveRects
          )

          createX = snapped.x
          createY = snapped.y
        }

        if (showGuides && !snapToGrid) {
          const anchor = params.anchor ?? 'center'
          const placedX = params.anchorPoint
            ? createX - params.anchorPoint.x
            : anchor === 'top-left'
              ? createX
              : createX - params.width / 2
          const placedY = params.anchorPoint
            ? createY - params.anchorPoint.y
            : anchor === 'top-left'
              ? createY
              : createY - params.height / 2
          const existing = mgr.getShapes()
          const otherBounds = existing
            .filter((s) => !s.hidden)
            .map((s) => ({ x: s.x, y: s.y, width: s.width, height: s.height }))
          const preResult = computeSmartGuidesForCreation(
            { x: placedX, y: placedY, width: params.width, height: params.height },
            otherBounds,
            snapGuideDistance,
            snapToGuides ? layoutSnapLinesForDragCreateRef.current : undefined
          )

          createX += preResult.snapAdjust.dx
          createY += preResult.snapAdjust.dy
        }

        const pathMirror = params.mirror
        const pathCommands = buildShapePathCommands(
          selectedShape,
          params.width,
          params.height,
          pathMirror
        )

        const created = mgr.addShape(selectedShape, createX, createY, {
          width: params.width,
          height: params.height,
          anchor: params.anchor ?? 'center',
          anchorPoint: params.anchorPoint,
          pathCommands,
          styleOverrides: {
            fill: styleOverrides.fill ?? '#e5e7eb',
            stroke: styleOverrides.stroke ?? '#000000',
            strokeWidth: styleOverrides.strokeWidth ?? 1,
          },
          selectAfterCreate: false,
        })

        if (showGuides && !snapToGrid && created) {
          const allShapes = mgr.getShapes()
          const otherBounds = allShapes
            .filter((s) => s.id !== created.id && !s.hidden)
            .map((s) => ({ x: s.x, y: s.y, width: s.width, height: s.height }))
          const bounds = {
            x: created.x,
            y: created.y,
            width: created.width,
            height: created.height,
          }
          const sgResult = computeSmartGuidesForCreation(
            bounds,
            otherBounds,
            snapGuideDistance,
            snapToGuides ? layoutSnapLinesForDragCreateRef.current : undefined
          )

          if (sgResult.guides.length > 0) {
            setActiveSmartGuides(sgResult.guides)
            setTimeout(() => setActiveSmartGuides([]), 400)
          }
        }
      }

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      dragCreateRef.current = null
      setDragCreate(null)
    },
    [
      selectedShape,
      computeShapeCreationParams,
      buildShapePathCommands,
      showGuides,
      snapGuideDistance,
      snapToGrid,
      snapToGuides,
      documentGridSnapStepPt,
      gridSnapLiveRects,
      setActiveSmartGuides,
      internalShapeManagerRef,
    ]
  )

  return {
    dragCreate,
    setDragCreate,
    dragCreateRef,
    dragRafRef,
    layoutSnapLinesForDragCreateRef,
    computeShapeCreationParams,
    buildShapePathCommands,
    handleShapeDragCreatePointerDown,
    handleShapeDragCreatePointerMove,
    handleShapeDragCreatePointerUp,
  }
}
